/**
 * SkuPreviewPanel — card-level "Preview a SKU" affordance (Phase 56.1 /
 * BLU-536 Plan 08).
 *
 * The per-row SkuTestSection validates ONE mapping at a time and cannot show
 * the dual story. This panel runs the Plan 06 per-SKU test through the org's
 * FULL saved mapping set (the anchor mapping's ns+key rides along; the
 * backend threads every other saved mapping plus the org's Design Shape map)
 * and renders BOTH composed filenames:
 *
 *   - Front + Back rows with side labels; "Dual print" badge when both
 *     design IDs are present.
 *   - AMBER when a dual product is missing a side's file, or when any
 *     metafield value is flagged (unrecognizedValues).
 *   - One flagged row per unrecognized value: purpose label + raw value +
 *     help naming the exact metafield to fix. `rawValue: ''` means the value
 *     is MISSING where the SKU encoding requires it.
 *
 * Icon + text pairing on every state (DESIGN.md §16: colour is never the
 * only signal). Anchored on the saved front print file mapping (back as a
 * fallback for back-only catalogs); without either saved the panel renders
 * a setup hint instead of the input.
 *
 * Motion: `useStaggeredTestPhase` (the canonical Test sequence) + the shared
 * `ResultBand` shell, keyed on result identity so `aria-live` announces once
 * per result, never on phase ticks.
 *
 * All buttons set `type="button"` explicitly — this panel renders inside the
 * parent OrganizationSettings `<form>` (nested-form audit, card rule #1).
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
  X,
} from 'lucide-react';
import { DTG_PLATEN_CODES, PLATEN_VARIANTS } from '@threadheads/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { useStaggeredTestPhase } from './useStaggeredTestPhase';
import { ResultBand, type BandKind } from './ResultBand';
import { PURPOSE_LABEL, PURPOSE_SHORT_LABEL, type Purpose } from './purposes';
import {
  SKU_INPUT_REGEX,
  type SkuTestResult,
  type UnrecognizedMetafieldValue,
} from './sku-test-result';

const TOAST_ID = {
  previewError: 'sku-preview-error',
  previewRateLimit: 'sku-preview-rate-limit',
} as const;

/**
 * Allowed-value hints for the purposes the backend value-validates. Mirrors
 * the allowlists in `@threadheads/shared` (Design Shapes) and the backend's
 * suffix/platen checks; phrasing matches PURPOSE_HELP in `./purposes`.
 */
/**
 * Join allowed values the way the hints read: "A or B" for two, "A, B, or C"
 * for three or more.
 */
function formatAllowedValues(values: readonly string[]): string {
  if (values.length <= 1) return values.join('');
  if (values.length === 2) return `${values[0]} or ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, or ${values[values.length - 1]}`;
}

const PLATEN_HINT = formatAllowedValues(DTG_PLATEN_CODES);
const PLATEN_VARIANT_HINT = formatAllowedValues(PLATEN_VARIANTS);

const EXPECTED_VALUES: Partial<Record<Purpose, string>> = {
  front_design_shape: 'Rectangular, Square, Horizontal, or Circle',
  back_design_shape: 'Rectangular, Square, Horizontal, or Circle',
  design_variant: 'Light, Dark, or Medium',
  front_design_variant: 'Light, Dark, or Medium',
  back_design_variant: 'Light, Dark, or Medium',
  // Built from the shared DTG list (ASCII x in the codes deliberately — the
  // platen storage format check `^[A-Za-z0-9_-]{1,32}$` rejects the ×
  // multiplication sign, so the hint must not coach operators into it).
  front_design_platen: PLATEN_HINT,
  back_design_platen: PLATEN_HINT,
  front_design_platen_variant: PLATEN_VARIANT_HINT,
  back_design_platen_variant: PLATEN_VARIANT_HINT,
};

/** Saved ns+key per persisted purpose, threaded from the card. */
export type SavedMappingsByPurpose = ReadonlyMap<
  Purpose,
  { namespace: string; key: string }
>;

interface SkuPreviewPanelProps {
  savedMappings: SavedMappingsByPurpose;
}

export function SkuPreviewPanel({ savedMappings }: SkuPreviewPanelProps) {
  const [sku, setSku] = useState('');
  const [result, setResult] = useState<SkuTestResult | null>(null);
  const { phase, start, reset, showWithMotion } = useStaggeredTestPhase();
  const utils = trpc.useUtils();

  // Anchor mapping — the per-SKU test endpoint takes ONE ns+key (the org's
  // other saved mappings are threaded server-side regardless). Front print
  // file is the natural anchor; back covers back-only catalogs. Passing a
  // SAVED row means the "override" equals the saved value, so the preview
  // reflects exactly what the sweep would compose.
  const anchorPurpose: Purpose = savedMappings.has('design_id')
    ? 'design_id'
    : 'back_design_id';
  const anchor = savedMappings.get(anchorPurpose) ?? null;

  const previewQuery = trpc.shopifyMetafieldMappings.testMappingForSku.useQuery(
    {
      sku,
      // Never fired while anchor is null (canPreview guards the refetch);
      // the empty strings only satisfy the hook's input type.
      namespace: anchor?.namespace ?? '',
      key: anchor?.key ?? '',
      purpose: anchorPurpose,
    },
    { enabled: false, retry: false },
  );

  const skuValid = SKU_INPUT_REGEX.test(sku);
  const showSpinner = phase === 'fetching' || phase === 'cooling';
  const canPreview = anchor !== null && skuValid && !showSpinner;

  const resetResult = () => {
    setResult(null);
    reset();
  };

  const handlePreviewClick = async () => {
    start();
    const res = await previewQuery.refetch();
    if (res.error) {
      const isRateLimited =
        (res.error.data?.code as string | undefined) === 'TOO_MANY_REQUESTS';
      if (isRateLimited) {
        toast.error(res.error.message, {
          duration: Infinity,
          id: TOAST_ID.previewRateLimit,
        });
      } else {
        // Transient operator-initiated check — non-persistent toast
        // (DESIGN.md §15.5 exception, same as the per-row Test).
        toast.error(`Preview failed: ${res.error.message}`, {
          id: TOAST_ID.previewError,
        });
      }
      reset();
      return;
    }
    if (!res.data) {
      reset();
      return;
    }
    // Const capture so the !undefined narrowing survives into the closure
    // (property narrowing on `res.data` is reset inside callbacks).
    const data = res.data;
    showWithMotion(() => {
      setResult(data);
    });
  };

  const handleSkuKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // ALWAYS preventDefault on Enter — otherwise it bubbles up and
      // submits the parent OrganizationSettings <form> (nested-form
      // footgun; card rule #1).
      e.preventDefault();
      if (canPreview) {
        void handlePreviewClick();
      }
    } else if (e.key === 'Escape') {
      if (sku || result) {
        e.preventDefault();
        setSku('');
        resetResult();
      }
    }
  };

  const handlePreviewThisOne = async (productGid: string) => {
    if (!anchor) return;
    start();
    try {
      const data =
        await utils.client.shopifyMetafieldMappings.testMappingForProductGid.query(
          {
            productGid,
            namespace: anchor.namespace,
            key: anchor.key,
            purpose: anchorPurpose,
          },
        );
      showWithMotion(() => {
        setResult({ ...data, sku: data.sku || sku });
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Preview failed: ${message}`, { id: TOAST_ID.previewError });
      reset();
    }
  };

  return (
    <div className="space-y-3 rounded-md border bg-card p-4">
      <div>
        <p className="text-sm font-medium">Preview a SKU</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Shows the front and back print files we{'’'}d compose for a
          SKU, using all your saved mappings.
        </p>
      </div>

      {anchor === null ? (
        <p className="text-xs text-muted-foreground">
          Save the front or back print file mapping first to preview a SKU.
        </p>
      ) : (
        <>
          <div className="flex w-full flex-wrap items-center gap-2">
            <Input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              onKeyDown={handleSkuKeyDown}
              placeholder="Enter a SKU"
              className="h-8 max-w-[260px] font-mono text-xs"
              aria-label="Preview composed print files for a specific SKU"
              aria-invalid={sku !== '' && !skuValid}
              disabled={showSpinner}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handlePreviewClick}
              disabled={!canPreview}
            >
              {showSpinner ? (
                <>
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  Previewing&hellip;
                </>
              ) : (
                'Preview'
              )}
            </Button>
          </div>

          {sku && !skuValid && (
            <p className="text-xs text-destructive">
              SKU must be 1-64 chars: letters, digits, dot, underscore, or
              hyphen.
            </p>
          )}

          {/* Single mount per result, keyed on result identity (ResultBand
              a11y discipline — see ResultBand.tsx header). */}
          {result && (
            <PreviewResultBand
              key={resultKey(result)}
              result={result}
              savedMappings={savedMappings}
              anchorNamespace={anchor.namespace}
              anchorKey={anchor.key}
              onPreviewThisOne={handlePreviewThisOne}
              animateIn={phase === 'showing'}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Result identity for the band's key — new identity replays the entrance
 *  animation + announces once; phase ticks on the same result do neither. */
function resultKey(result: SkuTestResult): string {
  if (result.status === 'present') {
    return [
      'present',
      result.sku,
      result.frontFilename ?? '',
      result.backFilename ?? '',
      result.unrecognizedValues
        .map((u) => `${u.purpose}=${u.rawValue}`)
        .join(','),
    ].join('|');
  }
  return `${result.status}|${result.sku}`;
}

// ---------------------------------------------------------------------------
// Result band — renders the four result states.
// ---------------------------------------------------------------------------

interface PreviewResultBandProps {
  result: SkuTestResult;
  savedMappings: SavedMappingsByPurpose;
  anchorNamespace: string;
  anchorKey: string;
  onPreviewThisOne: (productGid: string) => void;
  animateIn?: boolean;
}

function PreviewResultBand({
  result,
  savedMappings,
  anchorNamespace,
  anchorKey,
  onPreviewThisOne,
  animateIn = true,
}: PreviewResultBandProps) {
  if (result.status === 'variant_not_found') {
    return (
      <ResultBand kind="gray" animateIn={animateIn}>
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5">
            <Info className="h-4 w-4" aria-hidden="true" />
          </span>
          <p className="font-medium leading-tight">
            No variant with SKU <span className="font-mono">{result.sku}</span>{' '}
            found in your Shopify store. Check the SKU or add it in Shopify.
          </p>
        </div>
      </ResultBand>
    );
  }

  if (result.status === 'multiple_matches') {
    return (
      <ResultBand kind="amber" animateIn={animateIn}>
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="font-medium leading-tight">
              Found{' '}
              <span className="tabular-nums">{result.matches.length}</span>{' '}
              variants with SKU{' '}
              <span className="font-mono">{result.sku}</span>.
            </p>
            <ul className="space-y-1 text-xs">
              {result.matches.map((m, i) => (
                <li
                  key={m.productGid + i}
                  className="flex flex-wrap items-center gap-2 rounded border bg-background/60 px-2 py-1"
                >
                  <span className="min-w-0 flex-1 truncate font-mono">
                    {m.productTitle}
                    {m.variantTitle ? ` / ${m.variantTitle}` : ''}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onPreviewThisOne(m.productGid)}
                  >
                    Preview this one
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </ResultBand>
    );
  }

  if (result.status === 'not_present') {
    return (
      <ResultBand kind="amber" animateIn={animateIn}>
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
          </span>
          <p className="font-medium leading-tight">
            <span className="font-mono">{result.sku}</span> found on{' '}
            {result.productTitle || 'product'}: no value at{' '}
            <span className="font-mono">
              {anchorNamespace}.{anchorKey}
            </span>
            .
          </p>
        </div>
      </ResultBand>
    );
  }

  // ---------- present ----------
  const flagged = result.unrecognizedValues;
  const dualSideMissing =
    result.isDual &&
    (result.frontFilename === null || result.backFilename === null);
  const hasAnyFile =
    result.frontFilename !== null || result.backFilename !== null;

  const kind: BandKind =
    flagged.length > 0 || dualSideMissing
      ? 'amber'
      : hasAnyFile
      ? 'green'
      : 'gray';
  const icon =
    kind === 'green' ? (
      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
    ) : kind === 'amber' ? (
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
    ) : (
      <Info className="h-4 w-4" aria-hidden="true" />
    );

  return (
    <ResultBand kind={kind} animateIn={animateIn}>
      <div className="flex items-start gap-2 min-w-0">
        <span className="mt-0.5">{icon}</span>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="flex flex-wrap items-center gap-2 font-medium leading-tight">
            <span className="font-mono">{result.sku}</span>
            <span className="min-w-0 truncate">on {result.productTitle}</span>
            {result.isDual && (
              <Badge
                variant="secondary"
                className="text-[10px] uppercase tracking-wide"
              >
                Dual print
              </Badge>
            )}
          </p>

          {hasAnyFile || result.isDual ? (
            <div className="space-y-1">
              <SideRow
                side="Front"
                filename={result.frontFilename}
                missingOnDual={result.isDual && result.frontFilename === null}
              />
              <SideRow
                side="Back"
                filename={result.backFilename}
                missingOnDual={result.isDual && result.backFilename === null}
              />
            </div>
          ) : (
            <p className="text-xs opacity-80">
              No standard print file preview for this SKU (custom and
              non-standard SKUs resolve outside this lookup).
            </p>
          )}

          {flagged.length > 0 && (
            <div className="space-y-1.5">
              {flagged.map((flag) => (
                <FlaggedValueRow
                  key={`${flag.purpose}|${flag.rawValue}`}
                  flag={flag}
                  mapping={savedMappings.get(flag.purpose) ?? null}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ResultBand>
  );
}

// ---------------------------------------------------------------------------
// Side row — one per garment side, icon + text on the missing state.
// ---------------------------------------------------------------------------

interface SideRowProps {
  side: 'Front' | 'Back';
  filename: string | null;
  /** True when the product is dual but THIS side could not compose. */
  missingOnDual: boolean;
}

function SideRow({ side, filename, missingOnDual }: SideRowProps) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wider opacity-70">
        {side}
      </span>
      {filename ? (
        <span className="min-w-0 truncate font-mono tabular-nums">
          {filename}
        </span>
      ) : missingOnDual ? (
        <span className="inline-flex items-center gap-1 font-medium">
          <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
          Missing: no {side.toLowerCase()} file could be composed for this
          dual product.
        </span>
      ) : (
        <span className="opacity-70">No {side.toLowerCase()} print.</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flagged value row — purpose label + raw value + help naming the metafield.
// ---------------------------------------------------------------------------

interface FlaggedValueRowProps {
  flag: UnrecognizedMetafieldValue;
  /** The org's saved ns+key for the offending purpose, when mapped. */
  mapping: { namespace: string; key: string } | null;
}

function FlaggedValueRow({ flag, mapping }: FlaggedValueRowProps) {
  const metafieldName = mapping ? `${mapping.namespace}.${mapping.key}` : null;
  const expected = EXPECTED_VALUES[flag.purpose];
  const isMissing = flag.rawValue === '';

  return (
    <div className="flex items-start gap-2 rounded border bg-background/60 px-2 py-1.5 text-xs">
      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">
          {PURPOSE_LABEL[flag.purpose]}:{' '}
          {isMissing ? (
            'no value set'
          ) : (
            <>
              unrecognized value{' '}
              <span className="font-mono">{flag.rawValue}</span>
            </>
          )}
          .
        </p>
        <p className="opacity-80">
          {metafieldName
            ? isMissing
              ? `Set ${metafieldName} in Shopify for this product.`
              : `Fix the value at ${metafieldName} in Shopify.`
            : `Map and set the ${PURPOSE_SHORT_LABEL[flag.purpose]} metafield.`}
          {expected ? ` Expected ${expected}.` : ''}
        </p>
      </div>
    </div>
  );
}
